import React, { useState, useEffect } from 'react';
import { useAtom } from 'jotai';
import { isLoadingAtom, errorAtom } from '@/lib/store';
import { AuthCredentials } from '@/types';
import { useAuth } from '@/hooks/useAuth';
import { useSprintData } from '@/hooks/useSprintData';
import { AuthService } from '@/services/auth';

export default function LoginForm() {
  const { login, rememberMe, setRememberMe } = useAuth();
  const { fetchData } = useSprintData();
  const [isLoading, setIsLoading] = useAtom(isLoadingAtom);
  const [, setError] = useAtom(errorAtom);
  const storyPointField = 'customfield_10022';
  
  const [formData, setFormData] = useState<AuthCredentials>({
    email: '',
    apiToken: '',
    domain: '',
    boardId: ''
  });
  
  // Load saved credentials on component mount
  useEffect(() => {
    const savedCredentials = AuthService.loadCredentials();
    if (savedCredentials) {
      setFormData(savedCredentials);
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRememberMeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRememberMe(e.target.checked);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      // Add story point field to credentials
      const credentials = {
        ...formData,
        storyPointField
      };
      
      // Login with credentials
      await login(credentials, rememberMe);
      
      // Fetch sprint data
      await fetchData();
    } catch (err: unknown) {
      const error = err as Error;
      console.error('Authentication error:', error);
      setError(error.message || 'Failed to authenticate. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto p-8 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Connect to Atlassian</h2>
        <div className="w-16 h-1 bg-primary mx-auto mt-2 rounded-full"></div>
        <p className="mt-4 text-gray-600 dark:text-gray-300">
          Connect to your Atlassian account to view sprint planning and resource allocation across your team.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" htmlFor="email">
            Email
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
            value={formData.email}
            onChange={handleChange}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" htmlFor="apiToken">
            API Token
          </label>
          <input
            id="apiToken"
            name="apiToken"
            type="password"
            required
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
            value={formData.apiToken}
            onChange={handleChange}
          />
          <p className="text-xs mt-1 text-gray-500 dark:text-gray-400">
            Generate an API token from your{' '}
            <a 
              href="https://id.atlassian.com/manage-profile/security/api-tokens" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary hover:underline font-medium"
            >
              Atlassian Account Settings
            </a>
          </p>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" htmlFor="domain">
            Atlassian Domain
          </label>
          <div className="flex items-center rounded-md overflow-hidden border border-gray-300 dark:border-gray-600">
            <input
              id="domain"
              name="domain"
              type="text"
              required
              className="w-full px-4 py-2 border-0 bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
              placeholder="your-domain"
              value={formData.domain}
              onChange={handleChange}
            />
            <span className="px-3 py-2 bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300">
              .atlassian.net
            </span>
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" htmlFor="boardId">
            Board ID
          </label>
          <input
            id="boardId"
            name="boardId"
            type="text"
            required
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
            value={formData.boardId}
            onChange={handleChange}
          />
          <p className="text-xs mt-1 text-gray-500 dark:text-gray-400">
            The board ID can be found in the URL of your board: 
            https://your-domain.atlassian.net/jira/software/projects/XXX/boards/<strong>123</strong>
          </p>
        </div>
        
        <div className="flex items-center">
          <input
            id="rememberMe"
            name="rememberMe"
            type="checkbox"
            className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
            checked={rememberMe}
            onChange={handleRememberMeChange}
          />
          <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
            Remember my credentials
          </label>
        </div>
        
        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3 px-4 bg-primary hover:bg-opacity-90 text-white font-medium rounded-md cursor-pointer transition-all shadow-md hover:shadow-lg flex items-center justify-center"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Connecting...
            </>
          ) : (
            'Connect to Atlassian'
          )}
        </button>
      </form>
    </div>
  );
}